import React, { useRef } from 'react';
import styled from 'styled-components';
import defaultImg from '../../asset/project/default_1.png';
import { IndexOrderData } from '../../shared/dto/EntityRepository';
import { shrinkString } from '../../shared/util/shrinkString';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';

// Constants
const MAX_MAIN_TEXT_LENGTH = 24;
const HANDLE_ICON = '::';
const resolveOriginUrl = (originUrl: IndexOrderData['originUrl']) =>
  typeof originUrl === 'string' ? originUrl : originUrl?.src;

// Types
interface IndexCardProps {
  data: IndexOrderData;
  isDragging: boolean;
  isSelected: boolean;
  onDragStart: (data: IndexOrderData) => void;
  onDragEnd: () => void;
  onToggleSelect: (id: string) => void;
}

const IndexCard: React.FC<IndexCardProps> = ({
  data,
  isDragging,
  isSelected,
  onDragStart,
  onDragEnd,
  onToggleSelect,
}) => {
  const handleDragStart = () => onDragStart(data);
  const thumbContainerRef = useRef<HTMLDivElement>(null);
  const originUrl = resolveOriginUrl(data.originUrl);
  const imageUrl = useOptimizedImageUrl(originUrl ?? '', thumbContainerRef);

  const handleHandleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelect(data.id);
  };

  return (
    <CardContainer $isDragging={isDragging} $isSelected={isSelected} onClick={handleHandleClick}>
      <DragHandle>
        {HANDLE_ICON}
      </DragHandle>
      <DragBody
        draggable
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
      >
        <ThumbWrap ref={thumbContainerRef}>
          {originUrl !== undefined && (
            <ThumbImg
              src={imageUrl === '' ? defaultImg : imageUrl}
              alt=""
              $isPdf={data.subText === 'PDF'}
            />
          )}
        </ThumbWrap>
        <ContentContainer>
          <MainText>
            {shrinkString(data.mainText, MAX_MAIN_TEXT_LENGTH)}
          </MainText>
          <SubText>{data.subText}</SubText>
        </ContentContainer>
      </DragBody>
    </CardContainer>
  );
};

// Styled Components
const CardContainer = styled.div<{ $isDragging: boolean, $isSelected: boolean }>`
  width: 100%;
  height: ${({ $isDragging }) => $isDragging ? '10px' : '40px'};
  min-height: ${({ $isDragging }) => $isDragging ? '10px' : '40px'};
  transform: ${({ $isDragging }) =>
    $isDragging ? 'scaleY(0.2) scaleX(0.5)' : 'scaleY(1) scaleX(1)'
  };
  opacity: ${({ $isDragging }) => $isDragging ? '0.5' : '1'};

  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 0.5rem 10px;

  box-shadow: inset 1px 1px 3px 0 rgba(255, 255, 255, 0.35), inset -1px -1px 3px 0 rgba(0, 0, 0, 0.2);
  border-radius: 1px;
  transition: all 0.4s ease-out;
  border: ${({ $isSelected, theme }) => $isSelected ? `1px solid ${theme.colors.color_Gray_05}` : `0px solid transparent`};
  transition: all 0.4s ease-out;
`;

const DragHandle = styled.div`
  ${({ theme }) => theme.typography.Body_03_1};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const DragBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  cursor: grab;

  &:hover {
    transform: scale(0.98);
  }

  &:active {
    cursor: grabbing;
  }
`;

const ThumbWrap = styled.div`
  display: flex;
  align-items: center;
`;

const ThumbImg = styled.img <{ $isPdf: boolean }>`
  width: ${({ $isPdf }) => $isPdf ? '20px' : '24px'};
  height: ${({ $isPdf }) => $isPdf ? '20px' : '24px'};
  min-width: ${({ $isPdf }) => $isPdf ? '20px' : '24px'};
  min-height: ${({ $isPdf }) => $isPdf ? '20px' : '24px'};
  object-fit: contain;
  border-radius: 2px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: right;
  gap: 2px;
`;

const MainText = styled.div`
  ${({ theme }) => theme.typography.Body_04};
`;

const SubText = styled.div`
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

export default IndexCard;